import axios, { AxiosError, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios'
import { ElMessage } from 'element-plus'
import { useUserStore } from '@/stores/user'
import { fetchEventSource, type EventSourceMessage } from '@microsoft/fetch-event-source'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8001'

// Token 刷新配置
const REFRESH_THRESHOLD = 300 // 提前刷新阈值（秒），测试用 5 秒，生产建议 300 秒（5 分钟）

// Token 刷新管理器 - 使用 Promise 锁处理并发刷新
class TokenRefreshManager {
  private refreshPromise: Promise<boolean> | null = null

  /**
   * 获取刷新 Promise，如果正在刷新则返回现有的 Promise
   * 避免多个请求同时触发刷新
   */
  async refresh(): Promise<boolean> {
    if (this.refreshPromise) {
      return this.refreshPromise
    }

    this.refreshPromise = this.doRefresh()
    const result = await this.refreshPromise
    this.refreshPromise = null

    return result
  }

  /**
   * 方式 1：主动刷新 - 检查 token 是否即将过期，如果是则刷新
   * @param threshold 提前刷新阈值（秒），默认 REFRESH_THRESHOLD
   * @returns 是否已刷新
   */
  async refreshIfNeeded(threshold: number = REFRESH_THRESHOLD): Promise<boolean> {
    const userStore = useUserStore()
    if (userStore.shouldRefreshToken(threshold)) {
      return await this.refresh()
    }
    return false
  }

  /**
   * 实际执行刷新
   */
  private async doRefresh(): Promise<boolean> {
    try {
      const userStore = useUserStore()
      const result = await userStore.refreshAccessToken()
      return result.success
    } catch (error) {
      return false
    }
  }

  /**
   * 检查是否正在刷新
   */
  isRefreshing(): boolean {
    return this.refreshPromise !== null
  }

  /**
   * 清除登录状态并跳转
   */
  clearAndRedirect(): void {
    const userStore = useUserStore()
    userStore.clearToken()
    window.location.href = '/auth/login'
  }
}

const refreshManager = new TokenRefreshManager()

// 创建 axios 实例
const instance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  withCredentials: true, // 允许携带 Cookie
  headers: {
    'Content-Type': 'application/json'
  }
})

// 不需要 token 刷新的接口列表
const NO_REFRESH_URLS = [
  '/auth/login',
  '/auth/register',
  '/auth/refresh',
  '/auth/email/code',
  '/auth/logout',
  '/auth/verify-reset-code',
  '/auth/reset-password'
]

// 请求拦截器
instance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const userStore = useUserStore()
    const token = userStore.getToken()

    if (token) {
      // 方式 1：主动刷新 - token 即将过期
      // 排除登录相关接口和刷新接口
      const shouldSkipRefresh = NO_REFRESH_URLS.some(url => config.url?.includes(url))
      if (!shouldSkipRefresh) {
        const refreshed = await refreshManager.refreshIfNeeded()
        if (refreshed) {
          const newToken = userStore.getToken()
          config.headers.Authorization = `Bearer ${newToken}`
          return config
        }
      }
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error: AxiosError) => {
    return Promise.reject(error)
  }
)

// 响应拦截器
instance.interceptors.response.use(
  (response: AxiosResponse) => {
    const { code, msg, data } = response.data

    if (code === 0) {
      return data
    }

    // 处理未授权/Token 过期/Token 无效 - 构造 AxiosError 让错误拦截器处理刷新
    if (code === 4010 || code === 4011 || code === 4012) {
      const axiosError = new AxiosError(
        msg || '未登录',
        'ERR_UNAUTHORIZED',
        response.config,
        response.request,
        {
          ...response,
          status: 401,
          data: { code, msg }
        } as AxiosResponse
      )
      return Promise.reject(axiosError)
    }

    ElMessage.error(msg || '请求失败')
    return Promise.reject(new Error(msg || '请求失败'))
  },
  async (error: AxiosError) => {
    const { response, config } = error
    const originalRequest = config as InternalAxiosRequestConfig & { _retry?: boolean }

    if (!response) {
      ElMessage.error('网络错误')
      return Promise.reject(error)
    }

    const { status, data } = response

    // 只处理 401 状态码
    if (status !== 401) {
      ElMessage.error((data as any)?.msg || '请求失败')
      return Promise.reject(error)
    }

    // 如果是刷新 token 接口本身返回 401，直接跳转登录
    if (originalRequest.url?.includes('/auth/refresh')) {
      ElMessage.error('登录已过期，请重新登录')
      refreshManager.clearAndRedirect()
      return Promise.reject(error)
    }

    // 如果是 logout 接口返回 401，直接清除登录状态并返回成功（用户本来就是要退出）
    if (originalRequest.url?.includes('/auth/logout')) {
      refreshManager.clearAndRedirect()
      return Promise.resolve({ success: true } as any)
    }

    // 如果已经重试过，直接跳转登录
    if (originalRequest._retry) {
      ElMessage.error('登录已过期，请重新登录')
      refreshManager.clearAndRedirect()
      return Promise.reject(error)
    }

    // 标记已重试
    originalRequest._retry = true

    // 方式 2：被动刷新 - 收到 401 时刷新
    const refreshSuccess = await refreshManager.refresh()
    if (refreshSuccess) {
      const userStore = useUserStore()
      const newToken = userStore.getToken()
      if (newToken) {
        originalRequest.headers.Authorization = `Bearer ${newToken}`
        return instance(originalRequest)
      }
    }
    ElMessage.error('登录已过期，请重新登录')
    refreshManager.clearAndRedirect()
    return Promise.reject(new Error('刷新 token 失败'))
  }
)

// SSE 流式请求封装 - 使用 @microsoft/fetch-event-source
export interface SSEOptions<T = any> {
  onMessage: (data: T) => void
  onError?: (error: Error) => void
  onComplete?: () => void
  onOpen?: () => void
}

export interface SSEConfig {
  headers?: Record<string, string>
  /** 重试次数，默认 3 次 */
  retry?: number
  /** 重试间隔（毫秒），默认 1000ms */
  retryInterval?: number
}

export interface SSEController {
  /** 取消请求 */
  abort: () => void
  /** Promise，请求完成或出错时 resolve/reject */
  ready: Promise<void>
}

class RetriableError extends Error {}
class FatalError extends Error {}

/**
 * SSE 流式 POST 请求 - 使用 @microsoft/fetch-event-source
 *
 * 特性：
 * - 自动重连机制
 * - 支持 POST 请求体
 * - 可取消请求
 * - 更好的错误处理
 * - 支持 Token 刷新（401 时自动刷新并重连）
 *
 * @param url 请求地址
 * @param data 请求数据
 * @param options 回调选项
 * @param config 额外配置
 * @returns 控制器（可取消请求）
 *
 * 使用示例：
 * const ctrl = ssePost('/stream', { id: 1 }, {
 *   onMessage: (data) => console.log(data),
 *   onError: (err) => console.error(err),
 *   onComplete: () => console.log('done'),
 *   onOpen: () => console.log('connected')
 * })
 *
 * // 取消请求
 * ctrl.abort()
 *
 * // 等待完成
 * await ctrl.ready
 */
export function ssePost<T = any>(
  url: string,
  data?: any,
  options?: SSEOptions<T>,
  config?: SSEConfig
): SSEController {
  const abortController = new AbortController()
  const userStore = useUserStore()
  const maxRetries = config?.retry ?? 3
  const retryInterval = config?.retryInterval ?? 1000
  let retryCount = 0
  let completed = false
  let hasRefreshed = false

  const ready = new Promise<void>((resolve, reject) => {

    const fullUrl = `${API_BASE_URL}${url}`

    const getTokenBeforeConnect = async (): Promise<string | null> => {
      const token = userStore.getToken()
    
      if (token) {
        const refreshed = await refreshManager.refreshIfNeeded()
        if (refreshed) {
          return userStore.getToken()
        }
      } 
      return token
    }

    const doFetch = async (currentToken: string | null) => {
      fetchEventSource(fullUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': currentToken ? `Bearer ${currentToken}` : '',
          ...config?.headers
        },
        body: JSON.stringify(data),
        signal: abortController.signal,
        openWhenHidden: false,

        async onopen(response) {
          if (response.ok && response.headers.get('content-type')?.includes('text/event-stream')) {
            retryCount = 0
            hasRefreshed = false
            options?.onOpen?.()
            return
          }

          if (response.status === 401) {
            if (hasRefreshed) {
              ElMessage.error('登录已过期，请重新登录')
              refreshManager.clearAndRedirect()
              throw new FatalError('未登录或登录已过期')
            }

            const refreshSuccess = await refreshManager.refresh()

            if (refreshSuccess) {
              const newToken = userStore.getToken()
              hasRefreshed = true
              doFetch(newToken)
              return
            } else {
              ElMessage.error('登录已过期，请重新登录')
              refreshManager.clearAndRedirect()
              throw new FatalError('刷新 token 失败')
            }
          }

          if (response.status >= 400 && response.status < 500 && response.status !== 429) {
            throw new FatalError(`HTTP ${response.status}: ${response.statusText}`)
          } else {
            throw new RetriableError(`HTTP ${response.status}: ${response.statusText}`)
          }
        },

      onmessage(msg: EventSourceMessage) {
        // 处理结束事件 - 根据 event: close 关闭连接
        if (msg.event === 'close') {
          console.log('[SSE] 接收到 close 事件，关闭连接')
          if (!completed) {
            completed = true
            // 根据 data 判断是超时还是正常结束
            if (msg.data === 'timeout') {
              console.warn('[SSE] 连接超时')
              options?.onError?.(new Error('连接超时'))
            } else {
              options?.onComplete?.()
            }
          }
          abortController.abort()
          resolve()
          return
        }
        if (msg.data) {
          // 忽略 data: end 或 data: timeout 的非 JSON 数据
          if (msg.data === 'end' || msg.data === 'timeout') {
            return
          }
          try {
            const jsonData = JSON.parse(msg.data) as T
            options?.onMessage(jsonData)
          } catch (err) {
            console.error('[SSE] Parse error:', err, 'data:', msg.data)
          }
        }
      },

      onclose() {
        if (!completed) {
          completed = true
          options?.onComplete?.()
        }
        resolve()
      },

      onerror(err) {
        if (err instanceof FatalError) {
          options?.onError?.(err)
          reject(err)
          throw err // 致命错误，不再重试
        }

        retryCount++
        if (retryCount >= maxRetries) {
          const finalError = new Error(`Max retries (${maxRetries}) exceeded`)
          options?.onError?.(finalError)
          reject(finalError)
          throw finalError
        }

        // 返回重试间隔
        return retryInterval
      }
    }).catch((err) => {
      if (abortController.signal.aborted) {
        console.log('[SSE] Request aborted')
        resolve()
        return
      }
      console.error('[SSE] Error:', err)
      options?.onError?.(err)
      reject(err)
    })
    }

    // 开始请求，先检查是否需要主动刷新
    (async () => {
      const token = await getTokenBeforeConnect()
      doFetch(token)
    })()
  })

  return {
    abort: () => abortController.abort(),
    ready
  }
}

/**
 * SSE 流式 GET 请求
 *
 * 使用示例：
 * const ctrl = sseGet('/stream', {
 *   onMessage: (data) => console.log(data),
 *   onError: (err) => console.error(err),
 *   onComplete: () => console.log('done')
 * })
 */
export function sseGet<T = any>(
  url: string,
  options?: SSEOptions<T>,
  config?: SSEConfig
): SSEController {
  const abortController = new AbortController()
  const userStore = useUserStore()
  const maxRetries = config?.retry ?? 3
  const retryInterval = config?.retryInterval ?? 1000
  let retryCount = 0
  let hasRefreshed = false

  const ready = new Promise<void>((resolve, reject) => {
    const fullUrl = url.startsWith('/api') ? `${API_BASE_URL}${url}` : `${API_BASE_URL}/api${url}`

    const getTokenBeforeConnect = async (): Promise<string | null> => {
      const token = userStore.getToken()
      if (token) {
        const refreshed = await refreshManager.refreshIfNeeded()
        if (refreshed) {
          return userStore.getToken()
        }
      } 
      return token
    }

    const doFetch = async (currentToken: string | null) => {
      fetchEventSource(fullUrl, {
        method: 'GET',
        headers: {
          'Authorization': currentToken ? `Bearer ${currentToken}` : '',
          ...config?.headers
        },
        signal: abortController.signal,
        openWhenHidden: false,

        async onopen(response) {
          if (response.ok && response.headers.get('content-type')?.includes('text/event-stream')) {
            retryCount = 0
            hasRefreshed = false
            options?.onOpen?.()
            return
          }

          if (response.status === 401) {
            if (hasRefreshed) {
              ElMessage.error('登录已过期，请重新登录')
              refreshManager.clearAndRedirect()
              throw new FatalError('未登录或登录已过期')
            }

            const refreshSuccess = await refreshManager.refresh()
            if (refreshSuccess) {
              const newToken = userStore.getToken()
              hasRefreshed = true
              doFetch(newToken)
              return
            } else {
              ElMessage.error('登录已过期，请重新登录')
              refreshManager.clearAndRedirect()
              throw new FatalError('刷新 token 失败')
            }
          }

          if (response.status >= 400 && response.status < 500 && response.status !== 429) {
            throw new FatalError(`HTTP ${response.status}: ${response.statusText}`)
          } else {
            throw new RetriableError(`HTTP ${response.status}: ${response.statusText}`)
          }
        },

        onmessage(msg: EventSourceMessage) {
          if (msg.data) {
            try {
              const jsonData = JSON.parse(msg.data) as T
              options?.onMessage(jsonData)
            } catch (err) {
              console.error('[SSE] Parse error:', err)
            }
          }
        },

        onclose() {
          options?.onComplete?.()
          resolve()
        },

        onerror(err) {
          if (err instanceof FatalError) {
            options?.onError?.(err)
            reject(err)
            throw err
          }

          retryCount++
          if (retryCount >= maxRetries) {
            const finalError = new Error(`Max retries (${maxRetries}) exceeded`)
            options?.onError?.(finalError)
            reject(finalError)
            throw finalError
          }

          return retryInterval
        }
      }).catch((err) => {
        if (abortController.signal.aborted) {
          console.log('[SSE] Request aborted')
          resolve()
          return
        }
        console.error('[SSE] Error:', err)
        options?.onError?.(err)
        reject(err)
      })
    }

    // 开始请求，先检查是否需要主动刷新
    (async () => {
      const token = await getTokenBeforeConnect()
      doFetch(token)
    })()
  })

  return {
    abort: () => abortController.abort(),
    ready
  }
}

// 封装请求方法，确保返回类型正确
const request = {
  get: <T = any>(url: string, config?: any): Promise<T> => {
    return instance.get(url, config) as Promise<T>
  },
  post: <T = any>(url: string, data?: any, config?: any): Promise<T> => {
    return instance.post(url, data, config) as Promise<T>
  },
  put: <T = any>(url: string, data?: any, config?: any): Promise<T> => {
    return instance.put(url, data, config) as Promise<T>
  },
  delete: <T = any>(url: string, config?: any): Promise<T> => {
    return instance.delete(url, config) as Promise<T>
  },
  // SSE 流式 POST 请求
  ssePost: ssePost
}

export default request
